import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, doc, updateDoc, Timestamp, orderBy,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { payoutMethodLabels } from '../types';
import { getSalesPeriodForDate } from '../utils/salesPeriod';
import { todayBusinessDate , resolveBoundaryHour } from '../utils/businessDate';

/**
 * 日払い（当日精算）台帳。
 *
 * 夜職では出勤当日に一部を現金で渡す運用が普通にあるが、
 * 記録する場所が無いと締めのときに二重払いになる。
 * ここで起票した額は、当期の支給額から前払いとして差し引く。
 *
 * オーナー / 責任者の PC 操作前提（rules でも create/update を限定）。
 */

interface PayoutRow {
  id: string;
  staffId: string;
  staffName: string;
  businessDate: string;
  salesPeriodKey: string;
  amount: number;
  method: 'cash' | 'transfer' | 'other';
  status: 'paid' | 'cancelled';
  paidByName: string;
  note?: string;
  createdAt: Date;
}

interface StaffBalance {
  staffId: string;
  staffName: string;
  /** 当期の確定バック（staffIncentives の progressiveBackAmount 合計） */
  earnedBack: number;
  /** 当期の日払い合計 */
  paidOut: number;
  /** 締めで渡す残り */
  remaining: number;
}

const Payouts: React.FC = () => {
  const { shop, user } = useAuth();
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [balances, setBalances] = useState<StaffBalance[]>([]);
  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ staffId: '', amount: 0, method: 'cash' as PayoutRow['method'], note: '' });

  const period = useMemo(() => {
    if (!shop) return null;
    return getSalesPeriodForDate(new Date(), shop.salesPeriod?.closingDay ?? 'monthEnd', {
      timezone: shop.timezone || 'Asia/Tokyo',
      boundaryHour: resolveBoundaryHour(shop),
    });
  }, [shop]);

  const load = useCallback(async () => {
    if (!shop || !period) return;
    setLoading(true);
    setError(null);
    try {
      const [payoutSnap, incentiveSnap, staffSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'payouts'),
          where('shopId', '==', shop.id),
          where('salesPeriodKey', '==', period.periodKey),
          orderBy('createdAt', 'desc'),
        )),
        getDocs(query(
          collection(db, 'staffIncentives'),
          where('shopId', '==', shop.id),
          where('salesPeriodKey', '==', period.periodKey),
        )),
        getDocs(query(
          collection(db, 'employees'),
          where('shopId', '==', shop.id),
          where('status', '==', 'active'),
        )),
      ]);

      const payouts: PayoutRow[] = payoutSnap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          staffId: data.staffId ?? '',
          staffName: data.staffName ?? '',
          businessDate: data.businessDate ?? '',
          salesPeriodKey: data.salesPeriodKey ?? '',
          amount: data.amount ?? 0,
          method: data.method ?? 'cash',
          status: data.status ?? 'paid',
          paidByName: data.paidByName ?? '',
          note: data.note ?? undefined,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
        };
      });
      setRows(payouts);

      const earned = new Map<string, { name: string; back: number }>();
      incentiveSnap.docs.forEach((d) => {
        const data = d.data() as any;
        if (data.isShopShare || data.isUnassigned || !data.staffId) return;
        const back = (data.progressiveBackAmount ?? 0) || (data.backAmount ?? 0);
        const cur = earned.get(data.staffId) ?? { name: data.staffName ?? '', back: 0 };
        cur.back += back;
        earned.set(data.staffId, cur);
      });

      const out = new Map<string, number>();
      payouts.filter((p) => p.status === 'paid').forEach((p) => {
        out.set(p.staffId, (out.get(p.staffId) ?? 0) + p.amount);
      });

      const ids = new Set<string>([...Array.from(earned.keys()), ...Array.from(out.keys())]);
      setBalances(Array.from(ids).map((id) => {
        const e = earned.get(id);
        const paidOut = out.get(id) ?? 0;
        const earnedBack = e?.back ?? 0;
        return {
          staffId: id,
          staffName: e?.name || payouts.find((p) => p.staffId === id)?.staffName || id,
          earnedBack,
          paidOut,
          remaining: earnedBack - paidOut,
        };
      }).sort((a, b) => b.remaining - a.remaining));

      setStaff(staffSnap.docs.map((d) => ({
        id: d.id, name: (d.data() as any).displayName || '',
      })).filter((s) => s.name));
    } catch (e: any) {
      setError(e?.message ?? '日払いの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [shop, period]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!shop || !period || !form.staffId || form.amount <= 0) return;
    try {
      await addDoc(collection(db, 'payouts'), {
        shopId: shop.id,
        staffId: form.staffId,
        staffName: staff.find((s) => s.id === form.staffId)?.name ?? '',
        businessDate: todayBusinessDate(shop.timezone || 'Asia/Tokyo', resolveBoundaryHour(shop)),
        salesPeriodKey: period.periodKey,
        amount: form.amount,
        method: form.method,
        status: 'paid',
        paidBy: user?.uid ?? '',
        paidByName: user?.email ?? '',
        note: form.note || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      setAddOpen(false);
      setForm({ staffId: '', amount: 0, method: 'cash', note: '' });
      await load();
    } catch (e: any) {
      setError(e?.message ?? '日払いの記録に失敗しました');
    }
  };

  const cancel = async (row: PayoutRow) => {
    try {
      await updateDoc(doc(db, 'payouts', row.id), {
        status: 'cancelled',
        cancelledAt: Timestamp.now(),
        cancelledBy: user?.uid ?? '',
        updatedAt: Timestamp.now(),
      });
      await load();
    } catch (e: any) {
      setError(e?.message ?? '取消に失敗しました');
    }
  };

  const totals = useMemo(() => ({
    paidOut: rows.filter((r) => r.status === 'paid').reduce((s, r) => s + r.amount, 0),
    remaining: balances.reduce((s, b) => s + Math.max(0, b.remaining), 0),
    over: balances.filter((b) => b.remaining < 0),
  }), [rows, balances]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">日払い（当日精算）</Typography>
          <Typography variant="body2" color="text.secondary">
            対象期間: {period?.label ?? '—'}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setAddOpen(true)}>日払いを記録</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">当期の日払い合計</Typography>
          <Typography variant="h5" fontWeight="bold">¥{totals.paidOut.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">締めで渡す残り</Typography>
          <Typography variant="h5" fontWeight="bold">¥{totals.remaining.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, borderLeft: totals.over.length ? '4px solid' : undefined, borderColor: 'error.main' }}>
          <Typography variant="caption" color="text.secondary">払い過ぎ</Typography>
          <Typography variant="h5" fontWeight="bold" color={totals.over.length ? 'error.main' : undefined}>
            {totals.over.length} 名
          </Typography>
        </Paper>
      </Stack>

      {totals.over.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          日払いが当期のバックを超えているスタッフがいます:{' '}
          {totals.over.map((b) => `${b.staffName}（¥${Math.abs(b.remaining).toLocaleString()} 超過）`).join(' / ')}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>スタッフ別の残り</Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>スタッフ</TableCell>
                  <TableCell align="right">当期バック</TableCell>
                  <TableCell align="right">日払い済み</TableCell>
                  <TableCell align="right">締めで渡す残り</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.length === 0 ? (
                  <TableRow><TableCell colSpan={4}>当期のデータがありません。</TableCell></TableRow>
                ) : balances.map((b) => (
                  <TableRow key={b.staffId} hover>
                    <TableCell>{b.staffName}</TableCell>
                    <TableCell align="right">¥{b.earnedBack.toLocaleString()}</TableCell>
                    <TableCell align="right">¥{b.paidOut.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: b.remaining < 0 ? 'error.main' : undefined }}>
                      ¥{b.remaining.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>日払いの記録</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>営業日</TableCell>
                  <TableCell>スタッフ</TableCell>
                  <TableCell align="right">金額</TableCell>
                  <TableCell>方法</TableCell>
                  <TableCell>渡した人</TableCell>
                  <TableCell>メモ</TableCell>
                  <TableCell>状態</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8}>記録がありません。</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} hover sx={{ opacity: r.status === 'cancelled' ? 0.5 : 1 }}>
                    <TableCell>{r.businessDate}</TableCell>
                    <TableCell>{r.staffName}</TableCell>
                    <TableCell align="right">¥{r.amount.toLocaleString()}</TableCell>
                    <TableCell>{payoutMethodLabels[r.method]}</TableCell>
                    <TableCell>{r.paidByName}</TableCell>
                    <TableCell>{r.note ?? '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status === 'paid' ? '支払済' : '取消'}
                        color={r.status === 'paid' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" disabled={r.status !== 'paid'} onClick={() => cancel(r)}>
                        取消
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>日払いを記録</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="スタッフ" value={form.staffId}
              onChange={(e) => setForm({ ...form, staffId: e.target.value })} fullWidth>
              {staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField label="金額" type="number" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Math.max(0, Number(e.target.value)) })} fullWidth />
            <TextField select label="方法" value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value as PayoutRow['method'] })} fullWidth>
              <MenuItem value="cash">現金</MenuItem>
              <MenuItem value="transfer">振込</MenuItem>
              <MenuItem value="other">その他</MenuItem>
            </TextField>
            <TextField label="メモ" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} fullWidth />
            {form.staffId && (() => {
              const b = balances.find((x) => x.staffId === form.staffId);
              return b ? (
                <Alert severity={form.amount > b.remaining ? 'warning' : 'info'}>
                  当期バック ¥{b.earnedBack.toLocaleString()} / 日払い済み ¥{b.paidOut.toLocaleString()} /
                  残り ¥{b.remaining.toLocaleString()}
                  {form.amount > b.remaining && ' — この額は残りを超えます（前借りになります）'}
                </Alert>
              ) : null;
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={submit} disabled={!form.staffId || form.amount <= 0}>
            記録する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;
